import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Paper, Button, Checkbox, FormControlLabel, Alert } from '@mui/material';
import { Shield } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import userService from '../../services/userService';
import AppSnackbar from '../../components/shared/AppSnackbar';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../theme/theme';

export default function SafetyPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' | 'warning' });

  const handleAcknowledge = async () => {
    setLoading(true);
    try {
      await userService.acknowledgeSafety();
      await refreshUser();
      navigate('/');
    } catch (err) {
      console.warn('[SafetyPage] safety acknowledgment failed', err);
      setSnackbar({ open: true, message: 'Could not save your acknowledgment — please try again', severity: 'error' });
    }
    finally { setLoading(false); }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Shield sx={{ fontSize: 64, color: colors.primary.main, mb: 1 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{t('pages.safety.page_title')}</Typography>
      </Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Zero-Tolerance Policy</Typography>
        <Typography sx={{ mb: 2 }}>CoffeeMates is committed to providing a safe, respectful space for everyone. We operate a zero-tolerance policy for:</Typography>
        {['Sexual harassment — instant permanent ban', 'Physical assault or threats — instant permanent ban',
          'Stalking or unwanted contact — instant permanent ban', 'Intoxication — warning, then ban',
          'Disruptive behavior — graduated enforcement'].map((item, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, pl: 2 }}>• {item}</Typography>
        ))}
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Every participant has a "Report Abuse" button available during and after events. Reports are reviewed within 24 hours.
        </Alert>
      </Paper>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
        label="I understand and agree to the CoffeeMates safety policy"
        sx={{ mb: 2 }}
      />
      <Button fullWidth variant="contained" size="large" disabled={!checked || loading}
        onClick={handleAcknowledge} sx={{ py: 1.5 }}>
        {loading ? 'Saving...' : 'I Agree — Continue'}
      </Button>

      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
